<template>
    <div
        class="hts-form-textarea form-control"
        @click="$emit('click', $event)"
        @focus="$emit('focus', $event)">
        <label
            class="label"
            :for="name">
            <span class="label-text">
                {{ label }}<span
                    v-if="required"
                    class="text-error-500 ml-1">
                    *
                </span>
            </span>
            <span
                v-if="altLabel"
                class="label-text-alt">
                {{ altLabel }}
            </span>
        </label>
        <textarea
            :id="name"
            ref="input"
            class="textarea textarea-bordered"
            :class="cColorSize"
            :name="prop"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :required="required"
            :value="cValue"
            @input="($event) => onInput($event)"
            @paste="($event) => onPaste($event)" />
        <span class="label">
            <span
                v-if="errorMessage"
                class="label-text-alt text-rose-500">
                {{ errorMessage }}
            </span>
            <span
                v-if="description"
                class="label-text-alt">
                >{{ description }}
            </span>
        </span>
    </div>
</template>

<script lang="ts" setup>
import { useField, useFieldError } from 'vee-validate'
import { daisyuiColorSize } from '~/components/utils/daisyui'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: String,
  altLabel: String,
  description: String,
  name: String,
  placeholder: String,
  prop: String,
  rules: Object,
  size: String,
  color: String,
  max: {
    type: [Number, String],
    default: 0
  },
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean
})

const { prop, name, rules, modelValue, max } = toRefs(props)
const emits = defineEmits(['update:modelValue', 'input', 'paste'])
const refInput = templateRef<HTMLInputElement | null>('input', null)
const cColorSize = daisyuiColorSize('textarea', props)
let field: any
let errorMessage: undefined | ComputedRef
const cProp = computed(() => prop?.value)
const setFields = (prop: any) => {
  if (typeof prop === 'string' && prop !== '') {
    field = useField(prop, undefined, {
      label: name?.value || prop,
      validateOnValueUpdate: true,
      validateOnMount: false
    })
    errorMessage = useFieldError(cProp.value)
  }
}
watch(
  () => cProp.value,
  (val: any) => {
    setFields(val)
  }
)
onMounted(() => {
  setFields(cProp.value)
})
const cValue = computed({
  get: () => {
    return modelValue.value
  },
  set: (val) => {
    field?.setValue(val)
    emits('update:modelValue', val)
  }
})
watch(
  () => cValue.value,
  (val: any) => {
    field?.setValue(val)
  }
)
const onInput = () => {
  if (!(refInput && refInput.value)) {
    return
  }
  const val = refInput.value.value
  if (!props.disabled && !props.readonly) {
    if (typeof max.value === 'string') {
      max.value = Number(max.value)
    }
    if (max.value !== 0 && val.length > max.value) {
      cValue.value = val.substring(0, max.value)
      refInput.value.value = cValue.value
    } else {
      cValue.value = val
    }
  }
  emits('update:modelValue', val)
}
const onPaste = (e: any) => {
  if (!(refInput && refInput.value)) {
    return
  }
  const val = e.clipboardData.getData('text') as string
  if (!props.disabled && !props.readonly) {
    if (typeof max.value === 'string') {
      max.value = Number(max.value)
    }
    if (max.value !== 0 && val.length > max.value) {
      cValue.value = val.substring(0, max.value)
      refInput.value.value = cValue.value
    } else {
      cValue.value = val
    }
  }
  emits('update:modelValue', val)
  e.preventDefault()

  emits('paste', val)
}
</script>
